import { createColumnHelper } from '@tanstack/react-table'
import { INote } from 'models/contact_companies'
import { useAuth } from '../../../context/authentication/useAuth'
import { API_HOST } from 'utils/http-client'
import { AFbLink } from 'components/FbUI/FbLink/styles'
import { Editor } from 'components/Editor/Editor'
import { JsonIntoUl } from 'components/json-into-ul'
import { ActionButtons } from '../../Buttons/ActionButtons'
import { BiExpandAlt } from 'react-icons/bi'
import { NOTES_COLUMNS } from './notesColumnInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { TaglistCell } from 'components/FbUI/StagePill'

interface UseNotesTableColumnsProps {
  setActiveNote: (noteData: { note: INote; isViewing?: boolean } | null) => void
  handleNoteDelete: (noteId: number) => Promise<void>
  showDoorColumns?: boolean
}

export const useNotesTableColumns = ({
  setActiveNote,
  handleNoteDelete,
  showDoorColumns = false,
}: UseNotesTableColumnsProps) => {
  const { user: me } = useAuth()
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  const columnHelper = createColumnHelper<INote>()
  const notesColumnHelper = createTableColumnHelper({
    columns: NOTES_COLUMNS,
    tooltips,
  })

  const baseColumns = [
    columnHelper.accessor('user', {
      ...notesColumnHelper.retrieveColumn('user'),
      cell: (info) => {
        const row = info.row.original
        return !row.user
          ? `${me?.company.name} User`
          : row.user?.first_name + ' ' + row.user?.last_name
      },
      size: 100,
    }),
    columnHelper.accessor('content', {
      ...notesColumnHelper.retrieveColumn('content'),
      size: 340,
      cell: (info) => {
        const value = info.getValue()
        if (!value) return '--'

        return (
          <div
            className="max-h-[72px] overflow-hidden relative"
            style={{
              maskImage:
                'linear-gradient(to bottom, black 60%, transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(to bottom, black 60%, transparent 100%)',
            }}
          >
            <JsonIntoUl
              value={value}
              fallback={
                <Editor
                  value={value}
                  readOnly={true}
                  disableToolbar={true}
                  borderless={true}
                />
              }
            />
          </div>
        )
      },
    }),
    columnHelper.accessor('attachments', {
      ...notesColumnHelper.retrieveColumn('has_attachment'),
      cell: (info) => {
        const attachments = info.getValue()
        if (!attachments || attachments.length === 0) return '--'
        return (
          <div className="flex flex-col">
            {attachments?.map((attachment) => {
              const href = attachment?.file?.startsWith('/')
                ? `${API_HOST}${attachment.file}`
                : attachment.file
              return (
                <AFbLink
                  key={attachment.id}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {attachment.original_filename}
                </AFbLink>
              )
            })}
          </div>
        )
      },
      size: 150,
    }),
    columnHelper.accessor('created', {
      ...notesColumnHelper.retrieveColumn('created'),
      cell: (info) => {
        const date = info.getValue()
        if (!date) return '-'
        return new Date(date).toLocaleDateString()
      },
      size: 100,
    }),
    columnHelper.accessor('modified', {
      ...notesColumnHelper.retrieveColumn('modified'),
      cell: (info) => {
        const date = info.getValue()
        if (!date) return '-'
        return new Date(date).toLocaleDateString()
      },
      size: 100,
    }),
  ]

  const doorColumns = showDoorColumns
    ? [
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_country'),
          id: 'door_country',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (!doorProxy) return '--'
            return doorProxy.country || '--'
          },
          size: 120,
        }),
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_city'),
          id: 'door_city',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (!doorProxy) return '--'
            return doorProxy.city || '--'
          },
          size: 120,
        }),
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_state'),
          id: 'door_state',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (!doorProxy) return '--'
            return doorProxy.state || '--'
          },
          size: 100,
        }),
        columnHelper.display({
          ...notesColumnHelper.retrieveColumn('door_address'),
          id: 'door_address',
          cell: (info) => {
            const doorProxy = info.row.original.door_proxy
            if (!doorProxy) return '--'
            return doorProxy.address || '--'
          },
          size: 200,
        }),
        columnHelper.accessor('door_proxy', {
          ...notesColumnHelper.retrieveColumn('door_tags'),
          id: 'door_tags',
          cell: (info) => {
            const doorProxy = info.getValue()
            if (
              !doorProxy ||
              !doorProxy.taglist ||
              doorProxy.taglist.length === 0
            )
              return '--'

            return <TaglistCell taglist={doorProxy.taglist} />
          },
          size: 200,
        }),
      ]
    : []

  const actionColumn = columnHelper.display({
    ...notesColumnHelper.retrieveColumn('actions'),
    meta: {
      isEditable: false,
      isLastColumn: true,
      rightAlign: true,
    },
    cell: (info) => {
      const note = info.row.original
      const noteId = note.id
      return (
        <ActionButtons
          className={'justify-start'}
          onDelete={() => handleNoteDelete(noteId)}
          onEdit={() => setActiveNote({ note })}
          onSee={() => setActiveNote({ note, isViewing: true })}
          seeIcon={BiExpandAlt}
        />
      )
    },
    size: 120,
  })

  return [...baseColumns, ...doorColumns, actionColumn]
}
